<template>
    <section>
        <div v-show="visible" class="mb-4">
            <template v-if="modalMode">
                <p>
                    After tax income
                </p>
            </template>
            <template v-else>
                <income-guidance-summary :income-setup-mode="true"
                                         :suggested-amount-on-plsa-date="suggestedAmountOnPLSADate">
                </income-guidance-summary>
            </template>
            <number-input ref="baseIncomeNumberInput"
                          v-model="baseIncomeModel"
                          class="col-xl-4"
                          description="After tax income"
                          :min="Constants.MinAnnualIncome"
                          :max="Constants.MaxAnnualIncome">
            </number-input>
        </div>

        <div v-show="visible" class="mb-4">
            <p>
                Want some extra income in the first few years?
                <help title="First few years" :text="HelpText.ReqmntFirstFewYears()" />
            </p>
            <number-input ref="extraIncomeNumberInput"
                          v-model="extraIncomeModel"
                          class="col-xl-4"
                          description="Extra income"
                          :min="0"
                          :max="100000">
            </number-input>
        </div>

        <div v-show="visible" class="mb-4">
            <p>
                For how many years?
            </p>
            <div class="form-group">
                <div class="form-floating col-xl-4">
                    <dropdown ref="extraIncomeDurationDropDown"
                              :items="['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']"
                              v-model="extraIncomeYears"
                              :optional="extraIncomeYearsIsOptional"
                              description="Extra income years"
                              abbreviated-description="Extra income years"
                              classes="form-select form-control">
                    </dropdown>
                </div>
            </div>
        </div>

        <div v-if="visible && !modalMode">
            <p>
                When you're happy with your answers, click the 'Generate your income plan' button to see your income chart. 
                If you make any changes, just click the button again to update it!
            </p>
            <button type="button" class="btn btn-primary" @click="onPopulateIncomePlanClicked">
                Generate your income plan
            </button>
        </div>

        <div v-else-if="visible && modalMode">
            <div class="d-flex justify-content-end">
                <button type="button" class="btn btn-primary" @click="onPopulateIncomePlanClicked">Update your income plan</button>
                <button type="button" class="btn btn-secondary ms-2" @click="emit('cancelled')">Cancel</button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { ref, watch, computed } from 'vue';
    import NumberInput from "../common/NumberInput.vue";
    import Dropdown from '../common/Dropdown.vue';
    import IncomeGuidanceSummary from './IncomeGuidanceSummary.vue';
    import Help from "@/guiide/components/common/Help.vue";
    import HelpText from "@/guiide/ts/helpText";
    import { InitialIncomeProfile } from '@/guiide/ts/wants/initialIncomeProfile';
    import Constants from '@/guiide/ts/constants';

    defineExpose({ initialize, getIncome });

    const props = defineProps({
        suggestedAmountOnPLSADate: { type: Number, default: 0 },
        baseIncome: { type: Number, default: 0 },
        extraInitialIncome: { type: Number, default: 0 },
        visible: { type: Boolean, default: true },
        modalMode: { type: Boolean, default: false }
    });

    const emit = defineEmits<{
        (e: 'populate-income-plan', value: { baseIncome: number, extraIncome: number }): void,
        (e: 'cancelled'): void
    }>();

    const baseIncomeNumberInput = ref<typeof NumberInput>(null);
    const extraIncomeNumberInput = ref<typeof NumberInput>(null);
    const extraIncomeDurationDropDown = ref<typeof Dropdown>(null);
    const baseIncomeModel = ref(0);
    const extraIncomeModel = ref(0);
    const extraIncomeYears = ref('5');

    function initialize() {
        baseIncomeNumberInput.value.takeFocus();
    }

    watch(() => props.baseIncome, (newBaseIncome) => {
        baseIncomeModel.value = newBaseIncome;
    });

    watch(() => props.extraInitialIncome, (newExtraIncome) => {
        extraIncomeModel.value = newExtraIncome;
    });

    const extraIncomeYearsIsOptional = computed<boolean>(() => {
        return extraIncomeModel.value == 0;
    });

    function onPopulateIncomePlanClicked() {
        if (!(baseIncomeNumberInput.value.isValid()
            && extraIncomeNumberInput.value.isValid()
            && extraIncomeDurationDropDown.value.isValid())) {
            return
        }

        const eventArg: InitialIncomeProfile = {
            baseIncome: baseIncomeModel.value,
            extraIncome: extraIncomeModel.value,
            extraIncomeYears: parseInt(extraIncomeYears.value)
        };

        emit('populate-income-plan', eventArg);
    }

    function getIncome(): { baseIncome: number, extraIncome: number } {
        return { baseIncome: baseIncomeModel.value, extraIncome: extraIncomeModel.value };
    }
</script>